import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Import per-language JSON translation files
import en from '../locales/en.json';
import de from '../locales/de.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';
import es from '../locales/es.json';
import nl from '../locales/nl.json';
import sq from '../locales/sq.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '\u{1F1EC}\u{1F1E7}', native: 'English' },
  de: { name: 'German', flag: '\u{1F1E9}\u{1F1EA}', native: 'Deutsch' },
  fr: { name: 'French', flag: '\u{1F1EB}\u{1F1F7}', native: 'Fran\u00e7ais' },
  it: { name: 'Italian', flag: '\u{1F1EE}\u{1F1F9}', native: 'Italiano' },
  es: { name: 'Spanish', flag: '\u{1F1EA}\u{1F1F8}', native: 'Espa\u00f1ol' },
  nl: { name: 'Dutch', flag: '\u{1F1F3}\u{1F1F1}', native: 'Nederlands' },
  sq: { name: 'Albanian', flag: '\u{1F1E6}\u{1F1F1}', native: 'Shqip' },
};

const DEFAULT_LANGUAGE = 'en';

// Build UI_LABELS from imported JSON files
const UI_LABELS = { en, de, fr, it, es, nl, sq };

// Build CATEGORY_LABELS from the "categories" key in each JSON
const CATEGORY_LABELS = {
  en: en.categories || {},
  de: de.categories || {},
  fr: fr.categories || {},
  it: it.categories || {},
  es: es.categories || {},
  nl: nl.categories || {},
  sq: sq.categories || {},
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Detect language from URL path
  const detectLanguage = useCallback(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && SUPPORTED_LANGUAGES[pathParts[0]]) {
      return pathParts[0];
    }
    // Check localStorage for preference
    const saved = localStorage.getItem('preferred_language');
    if (saved && SUPPORTED_LANGUAGES[saved]) return saved;
    return DEFAULT_LANGUAGE;
  }, [location.pathname]);

  const [language, setLanguageState] = useState(detectLanguage);

  useEffect(() => {
    const detected = detectLanguage();
    setLanguageState(detected);
    
    // Auto-redirect: if user has a non-English language selected, and they're on
    // an English page that has a localized equivalent, redirect them.
    const pathParts = location.pathname.split('/').filter(Boolean);
    const hasLangPrefix = pathParts.length > 0 && SUPPORTED_LANGUAGES[pathParts[0]];
    
    if (detected !== DEFAULT_LANGUAGE && !hasLangPrefix) {
      // Check if this is a translatable public page
      const translatableRoutes = ['/', '/tours', '/daily-tours', '/self-guided-tours', '/blog', '/about', '/contact', '/terms', '/privacy'];
      const isTranslatable = translatableRoutes.some(r => location.pathname === r || (r !== '/' && location.pathname.startsWith(r + '/')));
      
      if (isTranslatable) {
        const localizedPath = `/${detected}${location.pathname}`;
        navigate(localizedPath, { replace: true });
      }
    }
  }, [detectLanguage, location.pathname, navigate]);

  const setLanguage = useCallback((lang) => {
    if (!SUPPORTED_LANGUAGES[lang]) return;
    localStorage.setItem('preferred_language', lang);
    setLanguageState(lang);
  }, []);

  // Get localized path for navigation
  const getLocalizedPath = useCallback((path, targetLang) => {
    const lang = targetLang || language;
    // Remove any existing language prefix
    const cleanPath = path.replace(/^\/(en|de|fr|it|es|nl|sq)\//, '/');
    if (lang === 'en') return cleanPath;
    return `/${lang}${cleanPath}`;
  }, [language]);

  // Get UI label with English fallback
  const t = useCallback((key) => {
    return UI_LABELS[language]?.[key] || UI_LABELS.en[key] || key;
  }, [language]);

  // Get translated category label
  const tc = useCallback((categoryId, fallback) => {
    const labels = CATEGORY_LABELS[language] || CATEGORY_LABELS.en;
    return labels[categoryId] || fallback || categoryId?.replace(/_/g, ' ') || '';
  }, [language]);

  const value = {
    language,
    setLanguage,
    getLocalizedPath,
    t,
    tc,
    isDefault: language === DEFAULT_LANGUAGE,
    languages: SUPPORTED_LANGUAGES,
    supportedCodes: Object.keys(SUPPORTED_LANGUAGES),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export { SUPPORTED_LANGUAGES, UI_LABELS, DEFAULT_LANGUAGE };
